import { apiRoutes } from '@/constants/api-routes';
import { apiRequest } from './http';
import type { AdminAffiliatesListView, AffiliateRequestView } from '@/types/api';

/**
 * Admin-only API surface for the affiliate program. Mirrors
 * `server/src/modules/payouts/admin-payouts.controller.ts`.
 *
 * Trigger-payout responds synchronously after the Stripe Transfer
 * completes — the round-trip is sub-second, and the synchronous
 * response gives the admin a clean success/failure signal in the UI.
 */
export const adminPayoutsService = {
  list(opts: { take?: number; skip?: number } = {}) {
    const params = new URLSearchParams();
    if (opts.take !== undefined) params.set('take', String(opts.take));
    if (opts.skip !== undefined) params.set('skip', String(opts.skip));
    const qs = params.toString();
    const path = qs ? `${apiRoutes.adminAffiliates.list}?${qs}` : apiRoutes.adminAffiliates.list;
    return apiRequest<AdminAffiliatesListView>(path);
  },
  triggerPayout(accountId: string) {
    return apiRequest<{ payoutId: string | null }>(
      apiRoutes.adminAffiliates.triggerPayout(accountId),
      { method: 'POST' },
    );
  },

  /** Pending "Request to Become an Affiliate" applications, oldest first. */
  listRequests() {
    return apiRequest<AffiliateRequestView[]>(apiRoutes.adminAffiliateRequests.list);
  },

  /** Approve or reject a pending request. Both decisions are terminal (204, no body). */
  decideRequest(accountId: string, decision: 'approved' | 'rejected') {
    return apiRequest<void>(apiRoutes.adminAffiliateRequests.decide(accountId), {
      method: 'PATCH',
      body: { decision },
    });
  },
};
